import { isAxiosError, type AxiosError } from 'axios';
import i18n from '@/shared/i18n';
import { deburr, words } from 'lodash';
import {
  Bdkv2OAuthFlow,
  Bdkv2ConceptScalarType,
  Bdkv2CredentialType,
  V2BookConnectionState,
  type Bdkv2Authentication,
  type Bdkv2BookAuthenticationDescriptor,
  type V2BookConnectionDescriptor,
  type V2BookConnectionResponse,
  type Bdkv2BookDescriptor,
  type Bdkv2CredentialDescriptor,
  type Bdkv2OAuthArgumentDescriptor,
  type Bdkv2BookDescriptorConfigMap,
  type Bdkv2ConceptDescriptor,
  type Bdkv2ConceptType,
  type Bdkv2Label,
  type Bdkv2Value,
} from '@/generated/hooks/bdk/models';
import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models/v1CredentialInfo';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models/v1AutomationStage';
import { getRoutePath } from '@/routes/util';
import { sortVersions } from '@/shared/utils/version-utils';
import { isExcludedBookName } from './constants';

export type TConnectionStage = 'draft' | 'published';

/** True when a connection is currently usable by an automation or agent. */
export const isConnectionReady = (
  connection?: V2BookConnectionDescriptor,
): boolean =>
  connection?.status?.state ===
  V2BookConnectionState.BOOK_CONNECTION_STATE_READY;

/**
 * Value the server returns in place of a saved sensitive credential. Sending it
 * back on update tells the server to keep the stored value.
 */
export const REDACTED_CREDENTIAL_VALUE = '<redacted>';

/**
 * Credential id of the deprecated browser connection method, which stores its
 * named credentials as one sensitive JSON string instead of declaring a
 * sensitive dictionary. Kept so connections created with it stay editable.
 */
const LEGACY_KEY_VALUE_CREDENTIAL_ID = 'credentials_json';

/**
 * True when a credential holds named key/value secrets and is edited with the
 * key/value editor rather than a single input.
 */
export const isKeyValueCredential = (
  credential: Bdkv2CredentialDescriptor,
): boolean =>
  credential.type === Bdkv2CredentialType.CredentialTypeSensitiveDictionary ||
  credential.id === LEGACY_KEY_VALUE_CREDENTIAL_ID;

/** True when a credential's value must be sent as a sensitive value. */
export const isSensitiveCredential = (
  credential: Bdkv2CredentialDescriptor,
): boolean =>
  credential.type === Bdkv2CredentialType.CredentialTypeSensitiveText ||
  credential.type === Bdkv2CredentialType.CredentialTypeSensitiveDictionary;

/**
 * Ids of the key/value credentials declared by a book's custom authentication
 * method, or an empty list when the method has none or does not exist.
 */
export const getKeyValueCredentialIds = (
  book: Bdkv2BookDescriptor,
  authMethodId: string,
): string[] =>
  (
    book.authentications?.find(auth => auth.custom?.id === authMethodId)?.custom
      ?.credentials ?? []
  )
    .filter(isKeyValueCredential)
    .map(credential => credential.id ?? '')
    .filter(Boolean);

/**
 * The custom authentication method a connection without one is bound to when
 * the user adds credentials to it: the first method with a key/value
 * credential, preferring non-deprecated methods.
 */
export const findKeyValueAuthentication = (
  book: Bdkv2BookDescriptor,
): Bdkv2BookAuthenticationDescriptor | undefined => {
  const candidates = (book.authentications ?? []).filter(auth =>
    auth.custom?.credentials?.some(isKeyValueCredential),
  );
  return candidates.find(auth => !auth.custom?.deprecated) ?? candidates[0];
};

/**
 * Credentials to send when updating `connection`: each key/value credential the
 * connection already has saved and the user did not edit is replaced with
 * {@link REDACTED_CREDENTIAL_VALUE}, so the server keeps the stored secrets
 * instead of the names-only values the edit form is prefilled with.
 */
export const keepUntouchedSavedKeyValueCredentials = (
  credentials: Record<string, string>,
  connection: V2BookConnectionDescriptor | undefined,
  keyValueCredentialIds: string[],
  isEdited: (credentialId: string) => boolean,
): Record<string, string> => {
  const savedCredentialIds = new Set(
    (connection?.authentication?.authentication_credentials ?? [])
      .filter(
        credential =>
          credential.value?.sensitive_value?.value?.text_value ===
          REDACTED_CREDENTIAL_VALUE,
      )
      .map(credential => credential.id),
  );
  const kept = { ...credentials };
  for (const id of keyValueCredentialIds) {
    if (savedCredentialIds.has(id) && !isEdited(id)) {
      kept[id] = REDACTED_CREDENTIAL_VALUE;
    }
  }
  return kept;
};

export interface IFilteredConnection {
  connection_id: string;
  connection_name: string;
  book_version?: string;
  has_published_connection?: boolean;
  has_draft_connection?: boolean;
  draft_connection?: V2BookConnectionDescriptor;
  published_connection?: V2BookConnectionDescriptor;
}

/**
 * Normalizes and processes authentication methods for a book.
 *
 * This function:
 * 1. Filters out deprecated authentication options so they're not offered to users
 * 2. Splits OAuth flows into separate authentication entries (authorization code and client credentials)
 * 3. Sorts authentication methods with OAuth types appearing first
 *
 * @param book - The book descriptor containing authentication configurations
 * @returns Array of normalized authentication method descriptors, sorted with OAuth first
 *
 * @example
 * const book = {
 *   name: 'salesforce',
 *   authentications: [{ oauth: { flows: ['authorization_code', 'client_credentials'] } }]
 * };
 * const methods = normalizeAuthenticationMethods(book);
 * // Returns: [{ oauth: { id: 'oauth_authorization_code', ... } }, { oauth: { id: 'oauth_client_credentials', ... } }]
 */
export const normalizeAuthenticationMethods = (book: Bdkv2BookDescriptor) => {
  const authMethods: Bdkv2BookAuthenticationDescriptor[] = [];
  book.authentications?.forEach(auth => {
    // Skip deprecated authentication options so they're not offered when
    // adding or editing a book connection.
    if (auth.oauth?.deprecated || auth.custom?.deprecated) {
      return;
    }
    if (auth.oauth) {
      // If oauth has multiple flows,
      // split them into separate entries with specific names to avoid them having the same name
      if (auth.oauth.flows && auth.oauth.flows.length > 1) {
        auth.oauth.flows.forEach(flow => {
          switch (flow) {
            case Bdkv2OAuthFlow.OAuthFlowAuthorizationCode:
              authMethods.push({
                ...auth,
                oauth: {
                  ...auth.oauth,
                  // Preserve original id from book descriptor (e.g., 'oauth', 'oauth_authorization_code')
                  id: auth.oauth?.id,
                  name: 'OAuth',
                  flows: [flow],
                  preferred: auth.oauth?.preferred,
                },
              });
              break;
            case Bdkv2OAuthFlow.OAuthFlowClientCredentials:
              authMethods.push({
                ...auth,
                oauth: {
                  ...auth.oauth,
                  // Preserve original id from book descriptor (e.g., 'oauth_client_credentials')
                  id: auth.oauth?.id,
                  name: 'Client Credentials',
                  flows: [flow],
                  preferred: auth.oauth?.preferred,
                },
              });
              break;
          }
        });
      } else {
        // Single flow - keep as is with original ID and Name
        authMethods.push(auth);
      }
    } else {
      authMethods.push(auth);
    }
  });

  // Sort authentication methods so OAuth items appear first
  return authMethods.sort((a, b) => {
    // OAuth items come first (priority 0)
    const aIsOAuth = !!a.oauth;
    const bIsOAuth = !!b.oauth;

    if (aIsOAuth && !bIsOAuth) return -1;
    if (!aIsOAuth && bIsOAuth) return 1;
    return 0;
  });
};

/**
 * Returns the configured id of an authentication method, regardless of whether
 * it's a custom or OAuth descriptor. Falls back to `undefined` for malformed
 * or absent descriptors so callers can default cleanly.
 */
export const getAuthMethodId = (
  auth: Bdkv2BookAuthenticationDescriptor | null | undefined,
): string | undefined => auth?.custom?.id ?? auth?.oauth?.id ?? undefined;

/**
 * Checks if an authentication method requires no credentials.
 *
 * @param auth - The authentication descriptor to check
 * @returns True if the authentication requires no credentials
 */
export const authRequiresNoCredentials = (
  auth: Bdkv2BookAuthenticationDescriptor,
): boolean => {
  if (auth.custom) {
    return !auth.custom.credentials || auth.custom.credentials.length === 0;
  }
  if (auth.oauth) {
    return !auth.oauth.credentials || auth.oauth.credentials.length === 0;
  }
  return false;
};

/**
 * Checks if an OAuth authentication should show a direct sign-in button.
 * Conditions: OAuth with authorization code flow, empty credentials, and empty arguments.
 *
 * @param auth - The authentication descriptor to check
 * @returns True if the authentication should show a direct sign-in button
 */
export const shouldShowDirectOAuthButton = (
  auth: Bdkv2BookAuthenticationDescriptor,
): boolean => {
  if (!auth.oauth) return false;

  const isAuthCodeFlow =
    auth.oauth.flows?.includes(Bdkv2OAuthFlow.OAuthFlowAuthorizationCode) ||
    false;
  const hasNoCredentials =
    !auth.oauth.credentials || auth.oauth.credentials.length === 0;
  const hasNoArguments =
    !auth.oauth.arguments || auth.oauth.arguments.length === 0;

  return isAuthCodeFlow && hasNoCredentials && hasNoArguments;
};

/**
 * Builds default values object for a credentials form based on credential descriptors.
 * Creates a key-value map where each credential ID is mapped to either its provided default value
 * or an empty string if no default is provided.
 *
 * @param credentials - Array of credential descriptors defining form fields
 * @param defaultValues - Optional record of pre-existing credential values
 * @returns Record mapping credential IDs to their default values (or empty strings)
 *
 * @example
 * const credentials = [
 *   { id: 'username', label: 'Username' },
 *   { id: 'password', label: 'Password' }
 * ];
 * const defaults = buildCredentialsFormDefaultValues(credentials, { username: 'admin' });
 * // Returns: { username: 'admin', password: '' }
 */
export const buildCredentialsFormDefaultValues = (
  credentials: Bdkv2CredentialDescriptor[],
  defaultValues: Record<string, string> | undefined,
) => {
  const formDefaultValues: Record<string, string> = {};
  credentials.forEach(credential => {
    formDefaultValues[credential.id!] = defaultValues?.[credential.id!] || '';
  });
  return formDefaultValues;
};

/**
 * Returns the list of credential fields needed for a connection form.
 *
 * This determines what fields to show based on the authentication type:
 * - OAuth flows: Returns client_id, client_secret, plus any additional OAuth arguments
 * - Custom auth: Returns the custom credentials defined in the auth method
 * - No auth selected: Returns empty array
 *
 * @param selectedOAuthFlow - The selected OAuth flow type (if OAuth is selected)
 * @param selectedAuth - The selected authentication method descriptor
 * @param authentications - All authentication methods from the integration
 * @returns Array of credential/argument descriptors for the form
 *
 * @example
 * // OAuth Client Credentials flow
 * getCredentialsForAuthMethod(Bdkv2OAuthFlow.OAuthFlowClientCredentials, null, authentications);
 * // Returns: [{ id: 'client_id', name: 'Client ID' }, { id: 'client_secret', name: 'Client Secret' }, ...oauthArgs]
 *
 * @example
 * // Custom authentication
 * getCredentialsForAuthMethod(null, { custom: { credentials: [...] } }, authentications);
 * // Returns: custom credentials array
 */
export const getCredentialsForAuthMethod = (
  selectedOAuthFlow: Bdkv2OAuthFlow | null | undefined,
  selectedAuth: Bdkv2BookAuthenticationDescriptor | null | undefined,
  authentications: Bdkv2BookAuthenticationDescriptor[] | undefined,
): (Bdkv2CredentialDescriptor | Bdkv2OAuthArgumentDescriptor)[] => {
  if (selectedOAuthFlow) {
    const oauthAuth = authentications?.find(auth => !!auth.oauth);
    const clientIdField: Bdkv2OAuthArgumentDescriptor = {
      id: 'client_id',
      name: 'Client ID',
    };
    const clientSecretField: Bdkv2OAuthArgumentDescriptor = {
      id: 'client_secret',
      name: 'Client Secret',
    };
    return [
      clientIdField,
      clientSecretField,
      ...(oauthAuth?.oauth?.arguments ?? []),
    ];
  }

  if (selectedAuth?.custom) {
    return selectedAuth.custom.credentials ?? [];
  }

  return [];
};

/**
 * Extracts credential values from a connection's authentication configuration.
 *
 * This transforms the connection's stored credentials into a flat key-value object
 * suitable for form initialization. Handles both plain text and sensitive values.
 *
 * @param connection - The book connection descriptor
 * @returns Object mapping credential IDs to their values (or empty string if no value)
 *
 * @example
 * // Connection with credentials
 * getConnectionInitialValues({
 *   authentication: {
 *     authentication_credentials: [
 *       { id: 'api_key', value: { text_value: 'my-key' } }
 *     ]
 *   }
 * });
 * // Returns: { api_key: 'my-key' }
 *
 * @example
 * // Connection with no credentials
 * getConnectionInitialValues({ authentication: {} });
 * // Returns: {}
 */
export const getConnectionInitialValues = (
  connection?: V2BookConnectionDescriptor,
): Record<string, string> => {
  if (!connection?.authentication?.authentication_credentials) {
    return {};
  }

  return connection.authentication.authentication_credentials.reduce(
    (acc, credential) => {
      const rawValue =
        credential.value?.text_value ||
        credential.value?.sensitive_value?.value?.text_value ||
        '';

      if (rawValue === REDACTED_CREDENTIAL_VALUE && credential.id) {
        const keysLabel = connection.labels?.find(
          l => l.key === `credential_keys_${credential.id}`,
        );
        if (keysLabel?.value) {
          try {
            // Prefer dot-separated format (current), fall back to JSON array (legacy)
            let keys: string[];
            try {
              const parsed = JSON.parse(keysLabel.value);
              keys = Array.isArray(parsed) ? parsed : [];
            } catch {
              keys = keysLabel.value.split('.').filter(Boolean);
            }
            acc[credential.id] = JSON.stringify(
              Object.fromEntries(keys.map(k => [k, ''])),
            );
            return acc;
          } catch {
            // fall through to default
          }
        }
      }

      acc[credential.id as string] = rawValue;
      return acc;
    },
    {} as Record<string, string>,
  );
};

/**
 * Interface representing formatted connection data for display purposes.
 * Used to structure connection information in a consistent way across the UI.
 */
export interface ConnectionDisplayData {
  connections: V2BookConnectionDescriptor[];
  connectionName: string;
  authenticationName?: string;
}

/**
 * Gets the human-readable display name for an authentication type.
 *
 * Returns:
 * - Translated "None" if no authentication is provided
 * - "OAuth" for OAuth authorization code flow
 * - "Client Credentials" for OAuth client credentials flow
 * - Custom authentication name from book descriptor for custom auth types
 *
 * @param book - The book descriptor containing authentication configurations
 * @param authentication - Optional authentication object with authentication_id
 * @returns Human-readable authentication type name
 *
 * @example
 * getAuthenticationTypeName(book, { authentication_id: 'oauth_authorization_code' });
 * // Returns: "OAuth"
 *
 * getAuthenticationTypeName(book, { authentication_id: 'api-key' });
 * // Returns: "API Key" (from book's custom auth configuration)
 */
export const getAuthenticationTypeName = (
  book: Bdkv2BookDescriptor,
  authentication?: Bdkv2Authentication,
): string => {
  if (!authentication) {
    return i18n.t('integrations.connections.noAuthentication', {
      ns: 'integrations',
    });
  }

  if (authentication.authentication_id?.startsWith('oauth')) {
    if (authentication.authentication_id === 'oauth_client_credentials') {
      return 'Client Credentials';
    }
    return 'OAuth';
  } else {
    return (
      book.authentications?.find(
        bookAuthentication =>
          bookAuthentication.custom?.id === authentication.authentication_id,
      )?.custom?.name ?? ''
    );
  }
};

/**
 * Transforms grouped integration connections into display-ready data format.
 * Extracts connection names from labels (with fallback to connection_id) and retrieves
 * authentication type names for each connection group.
 *
 * @param integration - The integration descriptor containing authentication configurations
 * @param integrationConnections - Array of connection groups (each group is an array of related connections)
 * @returns Array of ConnectionDisplayData objects ready for UI rendering
 *
 * @example
 * const connections = [[
 *   { connection_id: 'conn1', labels: [{ key: 'connection_name', value: 'My Connection' }] }
 * ]];
 * const displayData = transformIntegrationConnectionsToDisplayData(integration, connections);
 * // Returns: [{ connections: [...], connectionName: 'My Connection', authenticationName: 'OAuth' }]
 */
export const transformIntegrationConnectionsToDisplayData = (
  integration: Bdkv2BookDescriptor,
  integrationConnections: V2BookConnectionDescriptor[][],
): ConnectionDisplayData[] => {
  return integrationConnections.map(integrationConnection => {
    const connections = integrationConnection;
    const connectionName =
      connections[0].connection_name || connections[0].connection_id || '';

    return {
      connections,
      connectionName,
      authenticationName: getAuthenticationTypeName(
        integration,
        connections[0].authentication,
      ),
    };
  });
};

/**
 * Determines the authentication type category from an authentication ID.
 * Categorizes authentication into one of three types for different handling logic.
 *
 * @param authenticationId - Optional authentication identifier string
 * @returns Authentication type: 'oauth_authorization_code', 'oauth_client_credentials', or 'custom'
 *
 * @example
 * getAuthenticationType('oauth_authorization_code'); // Returns: 'oauth_authorization_code'
 * getAuthenticationType('api-key');                  // Returns: 'custom'
 * getAuthenticationType(undefined);                  // Returns: 'custom'
 */
export const getAuthenticationType = (
  authenticationId?: string,
): 'oauth_authorization_code' | 'oauth_client_credentials' | 'custom' => {
  if (authenticationId === 'oauth_authorization_code') {
    return 'oauth_authorization_code';
  }
  if (authenticationId === 'oauth_client_credentials') {
    return 'oauth_client_credentials';
  }
  return 'custom';
};

/**
 * Checks whether a connection is an OAuth authorization connection.
 *
 * A connection is considered OAuth authorization when:
 * 1. Its `authentication_id` starts with `'oauth'` (except `'oauth_client_credentials'`), OR
 * 2. Its `authentication_id` does not match any custom auth in the book descriptor
 *    (managed OAuth — the platform manages credentials via a UID).
 *
 * This mirrors the `AuthenticationName` / `getAuthenticationTypeName` logic:
 * explicit OAuth auth IDs return `'OAuth'`, managed OAuth auth IDs return `''`
 * (which `AuthenticationName` renders as "OAuth" fallback), while custom auth
 * IDs return their configured name.
 *
 * @param connection - The connection descriptor to check
 * @param integration - Optional book descriptor for custom auth lookup
 * @returns true if the connection uses an OAuth authorization flow
 */
export const isOAuthConnection = (
  connection: V2BookConnectionDescriptor,
  integration?: Bdkv2BookDescriptor,
): boolean => {
  const authId = connection.authentication?.authentication_id;
  if (!authId) return false;
  if (authId === 'oauth_client_credentials') return false;
  if (authId.startsWith('oauth')) return true;

  if (!integration) return false;
  const matchesCustomAuth = integration.authentications?.some(
    auth => auth.custom?.id === authId,
  );
  return !matchesCustomAuth;
};

/**
 * Determines whether a book offers any authentication method.
 *
 * This is only *half* the create gate, not the gate itself: an empty
 * `authentications` list is blocking only when a shared connection also
 * exists — see {@link hasSharedConnection} and
 * {@link isCreateSupersededBySharedConnection}.
 * On its own it still permits the credential-free flow (`NoAuthConnectionStep`).
 *
 * Callers should pass the book's *latest* version: that is the version a new
 * connection is offered against, and an older version having had auth methods
 * doesn't make the current one connectable.
 *
 * @param book - The book descriptor to check
 * @returns true when at least one authentication method is available
 *
 * @example
 * hasAuthenticationOptions({ authentications: [{ custom: { id: 'api-key' } }] }); // true
 * hasAuthenticationOptions({ authentications: [] });                              // false
 * hasAuthenticationOptions({});                                                   // false
 */
export const hasAuthenticationOptions = (book?: Bdkv2BookDescriptor): boolean =>
  (book?.authentications?.length ?? 0) > 0;

/**
 * Determines whether a connection is shared across every workspace in the
 * organization.
 *
 * Shared connections are provisioned centrally and are immutable from a
 * workspace's point of view: BDK rejects credential edits, upgrades, deletes
 * and discoverable changes on them. The UI hides those affordances rather than
 * disabling them, so a workspace user is never offered an action that can only
 * fail.
 *
 * Defaults to `false` when the field is absent, so a descriptor from an older
 * BDK (before `shared` existed) keeps behaving as a normal, editable
 * workspace connection.
 *
 * @param connection - The connection descriptor to check
 * @returns true when the connection is shared and therefore read-only
 *
 * @example
 * isSharedConnection({ shared: true });  // Returns: true
 * isSharedConnection({ shared: false }); // Returns: false
 * isSharedConnection({});                // Returns: false
 */
export const isSharedConnection = (
  connection?: V2BookConnectionDescriptor,
): boolean => connection?.shared === true;

/**
 * Determines whether any of the given connections is shared.
 *
 * Used together with {@link hasAuthenticationOptions} to decide whether a
 * workspace may create its own connection to a book: when the book offers no
 * authentication method *and* the organization has already provisioned a
 * shared one, there is nothing left for the user to create, so the create
 * affordance is disabled. Either condition on its own leaves creation open —
 * a no-auth book with no shared connection still has the credential-free
 * flow, and a book with auth methods can always take another connection.
 *
 * @param connections - The connections to scan
 * @returns true when at least one connection is shared
 *
 * @example
 * hasSharedConnection([{ shared: false }, { shared: true }]); // true
 * hasSharedConnection([{ shared: false }]);                   // false
 * hasSharedConnection(undefined);                             // false
 */
export const hasSharedConnection = (
  connections?: V2BookConnectionDescriptor[],
): boolean => connections?.some(isSharedConnection) === true;

/**
 * The create-connection rule in one place: a workspace may not create its own
 * connection when the book offers no authentication method *and* a shared
 * connection already covers it. Either condition alone leaves creation open —
 * a no-auth book with no shared connection still has the credential-free flow,
 * and a book with auth methods can always take another connection (the HTTP
 * case, whose shared connection happens to use no auth).
 *
 * Pass the book's *latest* version, and pass connections already scoped to
 * that book. An absent `book` reads as "no auth methods", so callers that may
 * not have resolved the descriptor yet should stay permissive themselves
 * rather than relying on this.
 *
 * @param book - The book's latest descriptor
 * @param connections - Existing connections for that book
 * @returns true when the create affordance should be disabled
 *
 * @example
 * const http = { authentications: [{ custom: { id: 'bearer' } }] };
 * isCreateSupersededBySharedConnection(http, [{ shared: true }]);          // false
 * isCreateSupersededBySharedConnection({ authentications: [] }, [{ shared: true }]); // true
 * isCreateSupersededBySharedConnection({ authentications: [] }, []);       // false
 */
export const isCreateSupersededBySharedConnection = (
  book?: Bdkv2BookDescriptor,
  connections?: V2BookConnectionDescriptor[],
): boolean =>
  !hasAuthenticationOptions(book) && hasSharedConnection(connections);

/**
 * Whether an integration offers triggers, counting both webhook triggers and
 * poll triggers.
 *
 * There is no matching check for actions: the catalog list doesn't include an
 * integration's procedures (they come from a separate endpoint), so every
 * integration is shown as offering actions.
 *
 * @param integration - The integration's descriptor
 * @returns true when the integration lists at least one trigger or poll
 *
 * @example
 * hasIntegrationTriggers({ triggers: [{}] }); // true
 * hasIntegrationTriggers({ polls: [{}] });    // true
 * hasIntegrationTriggers({});                 // false
 */
export const hasIntegrationTriggers = (
  integration: Bdkv2BookDescriptor,
): boolean =>
  Boolean(integration.triggers?.length || integration.polls?.length);

/**
 * Determines if an upgrade is available for a connection by comparing versions.
 * Checks if the connection's integration version differs from the latest available version.
 *
 * @param connection - The integration connection descriptor to check
 * @param latestIntegrationVersion - Optional latest available version of the integration
 * @returns true if upgrade is available (versions differ), false otherwise
 *
 * @example
 * const connection = { book_version: '1.0.0' };
 * isUpgradeAvailable(connection, '2.0.0'); // Returns: true
 * isUpgradeAvailable(connection, '1.0.0'); // Returns: false
 * isUpgradeAvailable(connection);          // Returns: false (no latest version provided)
 */
export const isUpgradeAvailable = (
  connection: V2BookConnectionDescriptor,
  latestIntegrationVersion?: string,
): boolean => {
  // Compare connection book version with latest available version
  return latestIntegrationVersion
    ? connection.book_version !== latestIntegrationVersion
    : false;
};

/**
 * Returns the OAuth scope list for the authentication method a connection uses,
 * looked up from the provided book descriptor.
 *
 * OAuth scope is defined on the book's OAuth authentication descriptor
 * (`book.authentications[].oauth.scope`), so the scope a connection effectively
 * uses is resolved by matching the connection's `authentication_id` against the
 * book's OAuth auth method id.
 *
 * @param connection - The connection descriptor
 * @param book - Optional book descriptor to resolve the scope from
 * @returns The OAuth scope array, or an empty array when none is found
 *
 * @example
 * getConnectionOAuthScope(
 *   { authentication: { authentication_id: 'oauth' } },
 *   { authentications: [{ oauth: { id: 'oauth', scope: ['read', 'write'] } }] },
 * );
 * // Returns: ['read', 'write']
 */
export const getConnectionOAuthScope = (
  connection: V2BookConnectionDescriptor,
  book?: Bdkv2BookDescriptor,
): string[] => {
  const authId = connection.authentication?.authentication_id;
  if (!authId || !book) {
    return [];
  }

  const oauthAuth = book.authentications?.find(
    auth => auth.oauth?.id === authId,
  );

  return oauthAuth?.oauth?.scope ?? [];
};

/**
 * Compares two scope lists irrespective of ordering and duplicates.
 *
 * @param a - First scope list
 * @param b - Second scope list
 * @returns true if both lists contain the same set of scopes
 */
const areScopesEqual = (a: string[], b: string[]): boolean => {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) {
    return false;
  }
  return [...setA].every(scope => setB.has(scope));
};

/**
 * Determines whether upgrading a connection to a newer book version requires the
 * user to re-authenticate.
 *
 * Re-authentication is only required for OAuth connections whose OAuth scope
 * changed between the current book version and the target version.
 *
 * @param connection - The connection descriptor being upgraded
 * @param currentBook - The book descriptor at the connection's current version
 * @param latestBook - The book descriptor at the target (upgrade) version
 * @returns true if the user must sign in again before the upgrade can proceed
 */
export const isReauthRequiredForUpgrade = (
  connection: V2BookConnectionDescriptor,
  currentBook?: Bdkv2BookDescriptor,
  latestBook?: Bdkv2BookDescriptor,
): boolean => {
  if (!isOAuthConnection(connection, currentBook)) {
    return false;
  }

  const currentScope = getConnectionOAuthScope(connection, currentBook);
  const latestScope = getConnectionOAuthScope(connection, latestBook);

  return !areScopesEqual(currentScope, latestScope);
};

/**
 * Checks if a connection is an OAuth redirection flow connection.
 * OAuth redirection flow connections have authentication_id "oauth" but no environment labels.
 * These connections cannot be edited as they use the OAuth redirect flow.
 *
 * @param connection - The integration connection descriptor to check
 * @returns true if the connection is OAuth redirection flow, false otherwise
 *
 * @example
 * const connection = { authentication: { authentication_id: 'oauth' }, labels: [] };
 * isOAuthRedirectionFlow(connection); // Returns: true
 *
 * const connection = { authentication: { authentication_id: 'oauth' }, labels: [{ key: 'draft_connection', value: 'true' }] };
 * isOAuthRedirectionFlow(connection); // Returns: false
 */
export const isOAuthRedirectionFlow = (
  connection: V2BookConnectionDescriptor,
  integration?: Bdkv2BookDescriptor,
): boolean => {
  if (!isOAuthConnection(connection, integration)) {
    return false;
  }

  // Check if draft_connection or published_connection label exists
  const hasConnectionLabel = connection.labels?.some(
    label =>
      label.key === 'draft_connection' || label.key === 'published_connection',
  );

  // OAuth redirection flow: OAuth auth but no environment labels
  return !hasConnectionLabel;
};

/**
 * Determines which actions are available for a connection based on its authentication type and version.
 *
 * Action availability rules:
 * - canReconnect: Only for OAuth connections (requires user interaction)
 * - canEdit: Always available — renaming is valid for all connection types
 * - canDelete: Always available for all connection types
 * - canUpgrade: Only when a newer book version is available
 * - canReactivate: Only when the connection was scaled to zero for inactivity
 *
 * A shared connection overrides all of the above: every flag comes back false,
 * because it is owned by the organization and immutable from a workspace.
 *
 * @param connection - The integration connection descriptor to evaluate
 * @param latestIntegrationVersion - Optional latest available version of the integration
 * @param integration - Optional book descriptor for accurate OAuth detection of managed auth
 * @returns Object with boolean flags for each available action
 *
 * @example
 * const connection = { authentication: { authentication_id: 'oauth_authorization_code' }, book_version: '1.0.0' };
 * const actions = getConnectionActions(connection, '2.0.0', integration);
 * // Returns: { canReconnect: true, canEdit: true, canDelete: true, canUpgrade: true, canReactivate: false }
 */
export const getConnectionActions = (
  connection: V2BookConnectionDescriptor,
  latestIntegrationVersion?: string,
  integration?: Bdkv2BookDescriptor,
) => {
  const oauthAuth = isOAuthConnection(connection, integration);
  const upgradeAvailable = isUpgradeAvailable(
    connection,
    latestIntegrationVersion,
  );

  // A shared connection belongs to the organization, not to this workspace, so
  // every action here is a write BDK will refuse. Report them all unavailable
  // and let the callers drop the affordances entirely — including reactivate,
  // which scales a pod the workspace doesn't own.
  if (isSharedConnection(connection)) {
    return {
      canReconnect: false,
      canEdit: false,
      canDelete: false,
      canUpgrade: false,
      canReactivate: false,
    };
  }

  return {
    canReconnect: oauthAuth,
    canEdit: true, // renaming is always valid regardless of auth type
    canDelete: true,
    canUpgrade: upgradeAvailable,
    canReactivate:
      connection?.status?.state ===
      V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE,
  };
};

/**
 * Generates a unique key for an integration by combining its name and version.
 * Used for identifying and grouping books in maps, caches, and UI components.
 *
 * @param integration - The integration descriptor containing name and version
 * @returns Unique key string in format "name-version"
 *
 * @example
 * const integration = { name: 'salesforce', version: '2.5.0' };
 * getIntegrationKey(integration); // Returns: 'salesforce-2.5.0'
 */
export const getIntegrationKey = (integration: Bdkv2BookDescriptor) => {
  return `${integration.name}`;
};

/**
 * Generates a unique book key from a connection descriptor.
 * Extracts integration name and version from the connection to create the same format as getIntegrationKey.
 * Useful for matching connections to their corresponding books.
 *
 * @param connection - The integration connection descriptor containing book_name and book_version
 * @returns Unique key string in format "book_name-book_version"
 *
 * @example
 * const connection = { book_name: 'salesforce', book_version: '2.5.0', connection_id: 'conn-123' };
 * getIntegrationKeyFromConnection(connection); // Returns: 'salesforce-2.5.0'
 *
 * // Can be used to match with getIntegrationKey result:
 * const book = { name: 'salesforce', version: '2.5.0' };
 * getIntegrationKey(book) === getIntegrationKeyFromConnection(connection); // true
 */
export const getIntegrationKeyFromConnection = (
  connection: V2BookConnectionDescriptor,
) => {
  return `${connection.book_name}`;
};

/**
 * Filters book connections by stage (draft or published) and returns
 * a simplified array with connection_id and connection_name.
 *
 * @param connections - Array of book connection descriptors
 * @param stage - The connection type stage to filter by ('draft' or 'published')
 * @returns Array of filtered connections with connection_id and connection_name
 *
 * @example
 * const connections = [
 *   {
 *     connection_id: 'openweather-nm8px',
 *     labels: [
 *       { key: 'connection_name', value: 'Test1' },
 *       { key: 'published_connection', value: 'true' }
 *     ]
 *   }
 * ];
 * const result = filterConnectionsByStage(connections, 'published');
 * // Returns: [{ connection_id: 'openweather-nm8px', connection_name: 'Test1' }]
 */
export function filterConnectionsByStage(
  connections: V2BookConnectionDescriptor[] | undefined,
  stage: TConnectionStage,
): IFilteredConnection[] {
  if (!connections || connections.length === 0) {
    return [];
  }

  return connections
    .filter(connection => {
      return connection.labels?.some(
        label => label.key === `${stage}_connection` && label.value === 'true',
      );
    })
    .map(connection => {
      const relatedConnections = connections.filter(
        c =>
          c.connection_name === connection.connection_name &&
          c.book_version === connection.book_version &&
          c.book_name === connection.book_name,
      );

      // Find the actual draft and published connections
      const draft_connection = relatedConnections.find(c =>
        c.labels?.some(
          label => label.key === 'draft_connection' && label.value === 'true',
        ),
      );

      const published_connection = relatedConnections.find(c =>
        c.labels?.some(
          label =>
            label.key === 'published_connection' && label.value === 'true',
        ),
      );

      return {
        connection_id: connection.connection_id || '',
        connection_name: connection.connection_name || '',
        book_version: connection.book_version || '',
        has_published_connection: Boolean(published_connection),
        has_draft_connection: Boolean(draft_connection),
        draft_connection,
        published_connection,
      };
    })
    .filter(
      connection => connection.connection_id && connection.connection_name,
    ) as IFilteredConnection[];
}

/**
 * Filters integrations to return only unique names with the semantically latest version.
 * For integrations with the same name, keeps only the one with the highest semantic version.
 *
 * @param integrations - Array of book descriptors to filter
 * @returns Array of book descriptors with unique names, each having the latest version
 *
 * @example
 * const integrations = [
 *   { name: 'salesforce', version: '1.0.0' },
 *   { name: 'salesforce', version: '2.0.0' },
 *   { name: 'slack', version: '1.0.0' }
 * ];
 * getLatestVersionIntegrations(integrations);
 * // Returns: [{ name: 'salesforce', version: '2.0.0' }, { name: 'slack', version: '1.0.0' }]
 */
export const getLatestVersionIntegrations = (
  integrations: Bdkv2BookDescriptor[],
): Bdkv2BookDescriptor[] => {
  // Group integrations by name
  const integrationsByName = new Map<string, Bdkv2BookDescriptor[]>();

  integrations.forEach(integration => {
    if (!integration.name) return;
    const existing = integrationsByName.get(integration.name) || [];
    integrationsByName.set(integration.name, [...existing, integration]);
  });

  // For each name, get the integration with the latest version
  return Array.from(integrationsByName.values()).map(integrationGroup => {
    const versions = integrationGroup.map(
      integration => integration.version || '0.0.0',
    );
    const sortedVersions = sortVersions(versions);
    const latestVersion = sortedVersions[0];
    return (
      integrationGroup.find(
        integration => integration.version === latestVersion,
      ) || integrationGroup[0]
    );
  });
};

/**
 * Interface representing grouped connections by book and book version.
 */
export interface IGroupedConnectionsByIntegration {
  book: string;
  book_version: string;
  connections: Array<{
    connection_alias: string;
    connection_id: string;
    production_credentials_available: boolean;
    test_credentials_available: boolean;
    production_connection_id?: string;
    test_connection_id?: string;
    connection_name?: string;
  }>;
}

/**
 * Groups connections by book and book_version, returning an array of integrations with their connections.
 *
 * Takes an array of credential info objects and groups them by book name and version,
 * returning a structured array where each entry contains the book name, version,
 * and an array of connections grouped by connection_alias with their IDs and credentials availability flags.
 *
 * Production credentials availability is determined by:
 * 1. Getting the connection_name from workspaceConnections by matching connection_id
 * 2. Checking if a matching published connection exists with the same connection_name, book_name, and book_version,
 *    and has a published_connection label.
 *
 * Test credentials availability is determined by:
 * 1. Getting the connection_name from workspaceConnections by matching connection_id
 * 2. Checking if a matching draft connection exists with the same connection_name, book_name, and book_version,
 *    and has a draft_connection label.
 *
 * @param connections - Array of credential info objects to group
 * @param workspaceConnections - Array of workspace book connection descriptors to check for credentials availability
 * @returns Array of grouped connections by integration, each containing book, book_version, and connections array grouped by connection_alias with production_credentials_available and test_credentials_available flags
 *
 * @example
 * const connections = [
 *   { book: 'salesforce', book_version: '2.0.0', connection_alias: 'conn1', connection_id: 'id1' },
 *   { book: 'salesforce', book_version: '2.0.0', connection_alias: 'conn2', connection_id: 'id2' },
 *   { book: 'slack', book_version: '1.0.0', connection_alias: 'conn3', connection_id: 'id3' }
 * ];
 * const workspaceConnections = [
 *   { connection_id: 'id1', connection_name: 'prod-conn1', book_name: 'salesforce', book_version: '2.0.0', labels: [{ key: 'published_connection', value: 'true' }] },
 *   { connection_id: 'id2', connection_name: 'test-conn2', book_name: 'salesforce', book_version: '2.0.0', labels: [{ key: 'draft_connection', value: 'true' }] }
 * ];
 * getConnectionsListByIntegrations(connections, workspaceConnections);
 * // Returns: [
 * //   {
 * //     book: 'salesforce',
 * //     book_version: '2.0.0',
 * //     connections: [
 * //       { connection_alias: 'conn1', connection_id: 'id1', production_credentials_available: true, test_credentials_available: false, production_connection_id: 'id1' },
 * //       { connection_alias: 'conn2', connection_id: 'id2', production_credentials_available: false, test_credentials_available: true, test_connection_id: 'id2' }
 * //     ]
 * //   },
 * //   {
 * //     book: 'slack',
 * //     book_version: '1.0.0',
 * //     connections: [{ connection_alias: 'conn3', connection_id: 'id3', production_credentials_available: false, test_credentials_available: false }]
 * //   }
 * // ]
 */
/**
 * Whether a code-extracted connection should be shown as one of an
 * automation's integrations.
 *
 * `book`/`book_version` are proto3 scalars, so the generated type marks them
 * required while the wire format omits them when empty — a connection can
 * arrive with `""` for either. Without both there is no book to resolve an
 * icon or a workspace connection against, so the entry can only render as a
 * nameless placeholder. `email` is excluded because it has its own trigger
 * dialog rather than an integration card. An empty-string alias is allowed:
 * it shows the connection without a modification menu.
 *
 * Shared so every surface listing an automation's integrations agrees on what
 * counts as one — the panel and the draft rail read the same
 * `extractAutomationCodeConnections` response, and a surface filtering it on
 * its own drifts into showing cards the others say don't exist.
 */
export const isDisplayableAutomationConnection = (
  connection: V1CredentialInfo,
): connection is V1CredentialInfo & { connection_alias: string } =>
  Boolean(connection.book) &&
  Boolean(connection.book_version) &&
  typeof connection.connection_alias !== 'undefined' &&
  !isExcludedBookName(connection.book);

export const getConnectionsListByIntegrations = (
  connections: readonly V1CredentialInfo[] | undefined,
  workspaceConnections: readonly V2BookConnectionDescriptor[] | undefined,
): IGroupedConnectionsByIntegration[] => {
  if (!connections || connections.length === 0) {
    return [];
  }

  // Group connections by book and book_version using a composite key
  const groupedMap = new Map<string, IGroupedConnectionsByIntegration>();

  connections.forEach(conn => {
    // See `isDisplayableAutomationConnection` for why each of these is skipped.
    if (!isDisplayableAutomationConnection(conn)) {
      return;
    }

    // Create composite key for grouping
    const key = `${conn.book}-${conn.book_version}`;

    // Get or create the group for this book/version combination
    let group = groupedMap.get(key);
    if (!group) {
      group = {
        book: conn.book,
        book_version: conn.book_version,
        connections: [],
      };
      groupedMap.set(key, group);
    }

    // Check if connection_alias already exists in the group
    const existingConnection = group.connections.find(
      c => c.connection_alias === conn.connection_alias,
    );

    if (existingConnection) {
      // Connection alias already exists, skip
      return;
    }

    // Get connection_name from workspaceConnections by matching connection_id
    const workspaceConnection = workspaceConnections?.find(
      workspaceConn => workspaceConn.connection_id === conn.connection_id,
    );

    const connectionName = workspaceConnection?.connection_name;

    // Find matching production connection to get its ID
    const productionConnection = workspaceConnections?.find(workspaceConn => {
      if (!connectionName) {
        return false;
      }

      // Match by connection_name, book_name, and book_version
      const nameMatches = workspaceConn.connection_name === connectionName;
      const bookNameMatches = workspaceConn.book_name === conn.book;
      const versionMatches = workspaceConn.book_version === conn.book_version;

      // Check if connection has published_connection label
      const hasPublishedLabel = workspaceConn.labels?.some(
        label => label.key === 'published_connection' && label.value === 'true',
      );

      return (
        nameMatches && bookNameMatches && versionMatches && hasPublishedLabel
      );
    });

    const hasProductionCredentials = Boolean(productionConnection);
    const productionConnectionId =
      productionConnection?.connection_id || undefined;

    // Find matching test connection to get its ID
    const testConnection = workspaceConnections?.find(workspaceConn => {
      if (!connectionName) {
        return false;
      }

      // Match by connection_name, book_name, and book_version
      const nameMatches = workspaceConn.connection_name === connectionName;
      const bookNameMatches = workspaceConn.book_name === conn.book;
      const versionMatches = workspaceConn.book_version === conn.book_version;

      // Check if connection has draft_connection label
      const hasDraftLabel = workspaceConn.labels?.some(
        label => label.key === 'draft_connection' && label.value === 'true',
      );

      return nameMatches && bookNameMatches && versionMatches && hasDraftLabel;
    });

    const hasTestCredentials = Boolean(testConnection);
    const testConnectionId = testConnection?.connection_id || undefined;

    group.connections.push({
      connection_alias: conn.connection_alias,
      connection_id: conn.connection_id || '',
      production_credentials_available: hasProductionCredentials,
      test_credentials_available: hasTestCredentials,
      production_connection_id: productionConnectionId,
      test_connection_id: testConnectionId,
      connection_name: connectionName,
    });
  });

  return Array.from(groupedMap.values());
};

/**
 * Filters discoverables based on fuzzy search over name and description.
 * Uses lodash deburr to normalize strings and words to tokenize search terms.
 * All search terms must match for a discoverable to be included.
 *
 * @param discoverables - Array of discoverable objects with name and description
 * @param searchTerm - Search string to filter by
 * @returns Filtered array of discoverables matching all search terms
 */
export const filterDiscoverablesBySearch = (
  discoverables: Array<{ name?: string; description?: string }>,
  searchTerm: string,
): typeof discoverables => {
  if (!searchTerm.trim()) {
    return discoverables;
  }

  const searchTerms = words(deburr(searchTerm.toLowerCase()));
  if (searchTerms.length === 0) {
    return discoverables;
  }

  return discoverables.filter(discoverable => {
    const name = deburr((discoverable.name || '').toLowerCase());
    const description = deburr((discoverable.description || '').toLowerCase());
    const combinedText = `${name} ${description}`;
    return searchTerms.every(term => combinedText.includes(term));
  });
};

/**
 * Sorts integrations to show connected integrations first, then non-connected.
 * Preserves original API response order within each group by filtering
 * into separate arrays and concatenating them.
 *
 * @param integrations - Array of book descriptors to sort
 * @param connectedIntegrations - Set of integration names that have connections
 * @returns Sorted array with connected integrations first, preserving order within each group
 *
 * @example
 * const integrations = [
 *   { name: 'slack', display_name: 'Slack' },
 *   { name: 'salesforce', display_name: 'Salesforce' },
 *   { name: 'github', display_name: 'GitHub' }
 * ];
 * const connectedIntegrations = new Set(['salesforce']);
 * const sorted = sortIntegrationsByConnectionStatus(integrations, connectedIntegrations);
 * // Returns: [{ name: 'salesforce', ... }, { name: 'slack', ... }, { name: 'github', ... }]
 */
export const sortIntegrationsByConnectionStatus = (
  integrations: Bdkv2BookDescriptor[],
  connectedIntegrations: Set<string>,
): Bdkv2BookDescriptor[] => {
  const connected = integrations
    .filter(integration => connectedIntegrations.has(integration.name!))
    .sort((a, b) => a.display_name!.localeCompare(b.display_name!));
  const notConnected = integrations
    .filter(integration => !connectedIntegrations.has(integration.name!))
    .sort((a, b) => a.display_name!.localeCompare(b.display_name!));
  return [...connected, ...notConnected];
};

// =============================================================================
// Config Map Utilities
// =============================================================================

/**
 * Supported form field types for config_map entries.
 */
export type TConfigMapFieldType = 'text' | 'number' | 'boolean' | 'enum';

/**
 * Represents an enum option for select dropdowns.
 */
export interface IConfigMapEnumOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Parsed config_map field information for form rendering.
 */
export interface IConfigMapFieldInfo {
  key: string;
  label: string;
  description?: string;
  fieldType: TConfigMapFieldType;
  isOptional: boolean;
  enumOptions?: IConfigMapEnumOption[];
}

/**
 * Checks if a book has a config_map with entries.
 *
 * @param integration - The book descriptor to check
 * @returns True if the book has a non-empty config_map
 *
 * @example
 * hasConfigMap({ config_map: { company_id: {...} } }); // Returns: true
 * hasConfigMap({ config_map: {} }); // Returns: false
 * hasConfigMap({}); // Returns: false
 */
export const hasConfigMap = (integration: Bdkv2BookDescriptor): boolean => {
  return !!(
    integration.config_map && Object.keys(integration.config_map).length > 0
  );
};

/**
 * Unwraps an optional type to get the inner type.
 * If the type is not optional, returns the original type.
 *
 * @param type - The concept type to unwrap
 * @returns The inner type if optional, otherwise the original type
 */
const unwrapOptionalType = (
  type: Bdkv2ConceptType | undefined,
): { innerType: Bdkv2ConceptType | undefined; isOptional: boolean } => {
  if (type?.optional_type?.inner) {
    return { innerType: type.optional_type.inner, isOptional: true };
  }
  return { innerType: type, isOptional: false };
};

/**
 * Determines the form field type from a Bdkv2ConceptType.
 * Handles optional types by unwrapping them first.
 *
 * @param type - The concept type to analyze
 * @returns The appropriate form field type ('text', 'number', 'boolean', 'enum')
 *
 * @example
 * getConfigMapFieldType({ scalar_type: 'ConceptScalarTypeNumber' }); // Returns: 'number'
 * getConfigMapFieldType({ optional_type: { inner: { enum_type: {...} } } }); // Returns: 'enum'
 */
export const getConfigMapFieldType = (
  type: Bdkv2ConceptType | undefined,
): TConfigMapFieldType => {
  const { innerType } = unwrapOptionalType(type);

  if (!innerType) {
    return 'text';
  }

  if (innerType.enum_type) {
    return 'enum';
  }

  if (innerType.scalar_type) {
    switch (innerType.scalar_type) {
      case Bdkv2ConceptScalarType.ConceptScalarTypeNumber:
        return 'number';
      case Bdkv2ConceptScalarType.ConceptScalarTypeBoolean:
        return 'boolean';
      case Bdkv2ConceptScalarType.ConceptScalarTypeText:
      default:
        return 'text';
    }
  }

  return 'text';
};

/**
 * Extracts enum options from a concept type.
 *
 * @param type - The concept type containing enum_type
 * @returns Array of enum options with value, label, and description
 */
export const getConfigMapEnumOptions = (
  type: Bdkv2ConceptType | undefined,
): IConfigMapEnumOption[] => {
  const { innerType } = unwrapOptionalType(type);

  if (!innerType?.enum_type?.members) {
    return [];
  }

  return innerType.enum_type.members.map(member => ({
    value: member.name || '',
    label: member.noun_phrase?.head || member.name || '',
    description: member.description,
  }));
};

/**
 * Extracts a human-readable display label from a config_map entry.
 * Uses the noun_phrases to construct a label, falling back to the key.
 *
 * @param configKey - The config_map key (used as fallback)
 * @param descriptor - The concept descriptor containing noun_phrases
 * @returns Human-readable label string
 *
 * @example
 * getConfigMapDisplayLabel('company_id', { noun_phrases: { noun_phrases: [{ modifiers: ['company'], head: 'id' }] } });
 * // Returns: "Company Id"
 */
export const getConfigMapDisplayLabel = (
  configKey: string,
  descriptor: Bdkv2ConceptDescriptor,
): string => {
  const nounPhrases = descriptor.noun_phrases?.noun_phrases;
  if (!nounPhrases || nounPhrases.length === 0) {
    // Convert snake_case to Title Case
    return configKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Use the first noun phrase
  const phrase = nounPhrases[0];
  const modifiers = phrase.modifiers || [];
  const head = phrase.head || '';

  // Combine modifiers and head, capitalize each word
  const words = [...modifiers, head].filter(Boolean);
  return words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Parses the config_map from a book descriptor into form field information.
 *
 * @param configMap - The config_map from the book descriptor
 * @returns Array of parsed field information for form rendering
 *
 * @example
 * parseConfigMapFields({ company_id: { type: { scalar_type: 'ConceptScalarTypeNumber' }, description: 'Company ID' } });
 * // Returns: [{ key: 'company_id', label: 'Company Id', fieldType: 'number', isOptional: false, ... }]
 */
export const parseConfigMapFields = (
  configMap: Bdkv2BookDescriptorConfigMap | undefined,
): IConfigMapFieldInfo[] => {
  if (!configMap) {
    return [];
  }

  return Object.entries(configMap).map(([key, descriptor]) => {
    const { isOptional } = unwrapOptionalType(descriptor.type);
    const fieldType = getConfigMapFieldType(descriptor.type);

    return {
      key,
      label: getConfigMapDisplayLabel(key, descriptor),
      description: descriptor.description,
      fieldType,
      isOptional,
      enumOptions:
        fieldType === 'enum'
          ? getConfigMapEnumOptions(descriptor.type)
          : undefined,
    };
  });
};

/**
 * Whether any config_map field must be filled in for the connection to be valid.
 *
 * Drives the section heading: the config_map block is labelled "(optional)"
 * because most integrations only expose tuning knobs there, but some — Anthropic
 * requires `model`, for example — put a genuinely mandatory field in the same
 * block. Calling that section optional tells the user they can skip a field the
 * form will then reject.
 *
 * @param fields - Parsed config_map fields, from `parseConfigMapFields`
 * @returns True if at least one field is required
 *
 * @example
 * hasRequiredConfigMapFields([{ isOptional: true, ... }]);  // Returns: false
 * hasRequiredConfigMapFields([{ isOptional: false, ... }]); // Returns: true
 */
export const hasRequiredConfigMapFields = (
  fields: readonly IConfigMapFieldInfo[],
): boolean => fields.some(field => !field.isOptional);

/**
 * Builds default values for a config_map form based on field descriptors.
 *
 * @param configMap - The config_map descriptor from the book
 * @param existingValues - Optional existing values to pre-populate
 * @returns Record mapping config keys to their default form values
 *
 * @example
 * buildConfigMapFormDefaults(
 *   { company_id: { type: { scalar_type: 'ConceptScalarTypeNumber' } } },
 *   { company_id: 123 }
 * );
 * // Returns: { company_id: 123 }
 */
export const buildConfigMapFormDefaults = (
  configMap: Bdkv2BookDescriptorConfigMap | undefined,
  existingValues?: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> => {
  if (!configMap) {
    return {};
  }

  const defaults: Record<string, string | number | boolean> = {};

  Object.entries(configMap).forEach(([key, descriptor]) => {
    // Use existing value if provided
    if (existingValues && key in existingValues) {
      defaults[key] = existingValues[key];
      return;
    }

    // Use default value from descriptor if available
    if (descriptor.default_value) {
      if (descriptor.default_value.text_value !== undefined) {
        defaults[key] = descriptor.default_value.text_value;
      } else if (descriptor.default_value.number_value !== undefined) {
        defaults[key] = descriptor.default_value.number_value;
      } else if (descriptor.default_value.boolean_value !== undefined) {
        defaults[key] = descriptor.default_value.boolean_value;
      }
      return;
    }

    // Set type-appropriate empty default
    const fieldType = getConfigMapFieldType(descriptor.type);
    switch (fieldType) {
      case 'number':
        defaults[key] = '';
        break;
      case 'boolean':
        defaults[key] = false;
        break;
      case 'enum':
      case 'text':
      default:
        defaults[key] = '';
    }
  });

  return defaults;
};

/**
 * Extracts config_map values from an existing connection's config_map.
 *
 * @param connection - The connection descriptor with config_map
 * @returns Record mapping config keys to their values
 *
 * @example
 * getConfigMapInitialValues({
 *   config_map: { company_id: { number_value: 123 }, environment: { text_value: 'SANDBOX' } }
 * });
 * // Returns: { company_id: 123, environment: 'SANDBOX' }
 */
export const getConfigMapInitialValues = (
  connection?: V2BookConnectionDescriptor,
): Record<string, string | number | boolean> => {
  if (!connection?.config_map) {
    return {};
  }

  const values: Record<string, string | number | boolean> = {};

  Object.entries(connection.config_map).forEach(([key, bdkValue]) => {
    if (bdkValue.text_value !== undefined) {
      values[key] = bdkValue.text_value;
    } else if (bdkValue.number_value !== undefined) {
      values[key] = bdkValue.number_value;
    } else if (bdkValue.boolean_value !== undefined) {
      values[key] = bdkValue.boolean_value;
    }
  });

  return values;
};

/**
 * Converts form values to the Bdkv2Value format expected by the API.
 * Uses the config_map descriptor to determine the correct value type.
 *
 * @param formValues - The form values to convert
 * @param configMap - The config_map descriptor from the book
 * @returns Record mapping config keys to Bdkv2Value objects
 *
 * @example
 * mapConfigMapToApiFormat(
 *   { company_id: 123, environment: 'SANDBOX' },
 *   { company_id: { type: { scalar_type: 'ConceptScalarTypeNumber' } }, environment: { type: { enum_type: {...} } } }
 * );
 * // Returns: { company_id: { number_value: 123 }, environment: { text_value: 'SANDBOX' } }
 */
export const mapConfigMapToApiFormat = (
  formValues: Record<string, string | number | boolean>,
  configMap: Bdkv2BookDescriptorConfigMap | undefined,
): Record<string, Bdkv2Value> => {
  if (!configMap) {
    return {};
  }

  const apiValues: Record<string, Bdkv2Value> = {};

  Object.entries(formValues).forEach(([key, value]) => {
    // Skip empty values for optional fields
    if (value === '' || value === undefined || value === null) {
      return;
    }

    const descriptor = configMap[key];
    if (!descriptor) {
      return;
    }

    const fieldType = getConfigMapFieldType(descriptor.type);

    switch (fieldType) {
      case 'number':
        apiValues[key] = {
          number_value:
            typeof value === 'number' ? value : parseFloat(String(value)),
        };
        break;
      case 'boolean':
        apiValues[key] = {
          boolean_value: typeof value === 'boolean' ? value : value === 'true',
        };
        break;
      case 'enum':
      case 'text':
      default:
        apiValues[key] = { text_value: String(value) };
    }
  });

  return apiValues;
};

/**
 * Why the backend rejected a connection as a duplicate:
 *  - `credentials`: another connection already uses the same credentials
 *    (possibly under a different name).
 *  - `name`: the connection name is already taken for this book/version/labels.
 */
export type DuplicateConnectionReason = 'credentials' | 'name';

export interface DuplicateConnectionError {
  reason: DuplicateConnectionReason;
  /** Backend `connection_id` slug of the existing connection (e.g. "sap-rfv8f"). */
  existingId: string;
}

/**
 * Reads the human-readable reason out of an API error. The backend surfaces it
 * either as an `error_message` header or in the response body (`error_message`
 * or `message`), so all four spellings are checked before falling back to the
 * Axios message.
 */
function getApiErrorMessage(error: AxiosError): string {
  const headers = error.response?.headers;
  const data = error.response?.data as Record<string, unknown> | undefined;
  return (
    headers?.['error_message'] ??
    headers?.['Error_message'] ??
    (typeof data?.error_message === 'string'
      ? data.error_message
      : undefined) ??
    (typeof data?.message === 'string' ? data.message : undefined) ??
    error.message ??
    ''
  );
}

/**
 * True when a connection upgrade was rejected because the connection already
 * sits on the requested book version ("New book version is the same as the
 * existing book version").
 *
 * The upgrade action is offered from a cached connections list, which is only
 * refreshed after a successful upgrade, so the row the user clicks can be one
 * version behind the server. Retrying then hits this 400 — nothing is wrong and
 * nothing is left to do, so callers treat it as a no-op rather than a failure.
 */
export function isConnectionAlreadyAtTargetVersionError(
  error: unknown,
): boolean {
  if (!isAxiosError(error) || error.response?.status !== 400) return false;
  return /same as the (?:existing|current) book version/i.test(
    getApiErrorMessage(error),
  );
}

/**
 * Both duplicate phrasings share "already exists" followed by a trailing
 * "Existing connection: <id>". Requiring them in that order keeps unrelated
 * messages that happen to contain either phrase from matching.
 */
const DUPLICATE_CONNECTION_PATTERN =
  /already exists\b(.*?)Existing connection:\s*(\S+)/i;

/**
 * Reads a backend duplicate-connection message. The single place that knows the
 * message's shape, so a backend rephrasing is a one-line fix here rather than a
 * hunt through every caller.
 *
 * @param message - Decoded backend message
 * @returns The reason and existing connection id, or undefined when the message
 *   is not a recognized duplicate
 */
export function parseDuplicateConnectionMessage(
  message: string,
): DuplicateConnectionError | undefined {
  const match = message.match(DUPLICATE_CONNECTION_PATTERN);
  if (!match) return undefined;

  return {
    reason: /same credentials/i.test(match[1]) ? 'credentials' : 'name',
    // The id ends the sentence, so drop the sentence's full stop.
    existingId: match[2].replace(/\.$/, ''),
  };
}

/**
 * Extracts duplicate-connection details from a 400 API error. The backend
 * rejects duplicates for two distinct reasons, each with its own message:
 *  - credentials: "...already exists with the same credentials..."
 *  - name/labels: "...already exists with the same book name, version,
 *    connection name and labels..."
 * Finds the message wherever the API put it, then defers to
 * `parseDuplicateConnectionMessage`. Returns the reason and existing connection
 * id, or undefined when the error is not a recognized duplicate.
 */
export function getDuplicateConnectionError(
  error: unknown,
): DuplicateConnectionError | undefined {
  if (!isAxiosError(error) || error.response?.status !== 400) return undefined;
  return parseDuplicateConnectionMessage(getApiErrorMessage(error));
}

/**
 * The distinctive clause of the backend's refusal. The full message is:
 *   "authentication is required to add your own connection for book <book>:
 *    connection <id> is already shared with every workspace, and a connection
 *    without credentials would duplicate it"
 */
const SHARED_CONNECTION_REQUIRES_CREDENTIALS_PATTERN =
  /already shared with every workspace/i;

/**
 * Detects the 401 the backend returns when a credential-less add is refused
 * because a shared connection already covers the book.
 *
 * Deliberately matches the message as well as the status. 401 alone is
 * ambiguous — an expired token, or a provider rejecting the credentials the
 * user just typed, arrives the same way — and the callers funnel every
 * create/update/publish path through one error handler. Claiming those are
 * "add credentials" would both mislead the user and suppress the Sentry
 * report, so an unrecognised 401 must fall through to the generic path.
 *
 * That makes a backend rephrasing degrade to the generic error rather than to
 * a wrong one, which is the safe direction to fail.
 *
 * @param error - The error thrown by a create/update connection call
 * @returns true when the user must supply credentials to proceed
 *
 * @example
 * // 401 carrying the shared-connection message
 * isCredentialsRequiredError(sharedConnection401); // true
 * // 401 from an expired token
 * isCredentialsRequiredError(expiredToken401);     // false
 */
export function isCredentialsRequiredError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 401) return false;
  return SHARED_CONNECTION_REQUIRES_CREDENTIALS_PATTERN.test(
    getApiErrorMessage(error),
  );
}

/**
 * Extracts the existing connection id from a duplicate *credentials* error.
 * Returns undefined for any other error, including name/label duplicates, so
 * callers can keep the "reuse your own draft" flow scoped to credential
 * matches.
 */
export function getDuplicateCredentialsConnectionName(
  error: unknown,
): string | undefined {
  const duplicate = getDuplicateConnectionError(error);
  return duplicate?.reason === 'credentials' ? duplicate.existingId : undefined;
}

export type DuplicateCredentialsClassification =
  | { kind: 'ownDraftDuplicate'; existingId: string }
  | { kind: 'otherConnectionDuplicate'; existingId: string }
  | { kind: 'unknown' };

/**
 * Decides whether a backend duplicate-credentials error is pointing at the
 * caller's own draft connection. The duplicate-credentials error reports the
 * backend `connection_id` slug (e.g. "twilio-tr2tq"), so we compare against
 * the draft's `connection_id`, not the user-typed `connection_name`.
 */
export function classifyDuplicateCredentialsError(
  error: unknown,
  draftConnectionId: string | undefined,
): DuplicateCredentialsClassification {
  const existingId = getDuplicateCredentialsConnectionName(error);
  if (!existingId) return { kind: 'unknown' };
  if (draftConnectionId && existingId === draftConnectionId) {
    return { kind: 'ownDraftDuplicate', existingId };
  }
  return { kind: 'otherConnectionDuplicate', existingId };
}

/**
 * Resolves a backend connection_id (e.g. "twilio-tr2tq") to its
 * connection_name using a known connections list, falling back to the id when
 * no match is found.
 */
export function resolveConnectionLabel(
  connections: V2BookConnectionDescriptor[] | undefined,
  connectionId: string,
): string {
  return (
    connections?.find(c => c.connection_id === connectionId)?.connection_name ??
    connectionId
  );
}

const DRAFT_PUBLISHED_LABEL_KEYS = new Set([
  'draft_connection',
  'published_connection',
]);

/**
 * Collapses a draft/published pair into the distinct connections it refers to,
 * dropping absent entries. A connection serving both environments is passed in
 * twice and comes back once, so consumers can tell a genuine pair from a single
 * shared connection.
 */
export function dedupeConnectionsById(
  connections: (V2BookConnectionDescriptor | undefined)[],
): V2BookConnectionDescriptor[] {
  const seen = new Set<string>();
  return connections.filter(
    (connection): connection is V2BookConnectionDescriptor => {
      const id = connection?.connection_id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    },
  );
}

/**
 * Finds the connection carrying an environment label within a draft/published
 * group. A single connection carrying both labels is returned for either key,
 * which is how "same credentials for test and production" shows up.
 */
export function findConnectionWithLabel(
  connections: V2BookConnectionDescriptor[] | undefined,
  labelKey: 'draft_connection' | 'published_connection',
): V2BookConnectionDescriptor | undefined {
  return connections?.find(connection =>
    connection.labels?.some(
      label => label.key === labelKey && label.value === 'true',
    ),
  );
}

/**
 * Which environments a single connection serves, read from its labels.
 *
 * A connection can carry both `draft_connection` and `published_connection`
 * (the "same credentials for Test and Production" case), exactly one, or
 * neither — OAuth redirection-flow connections have no environment label at
 * all, see {@link isOAuthRedirectionFlow}.
 */
export interface IConnectionEnvironmentLabels {
  hasDraft: boolean;
  hasPublished: boolean;
}

/**
 * Reads the draft/published environment labels off a connection.
 *
 * @param connection - The connection descriptor to inspect
 * @returns Flags for each environment label the connection carries
 *
 * @example
 * getConnectionEnvironmentLabels({
 *   labels: [{ key: 'draft_connection', value: 'true' }],
 * });
 * // Returns: { hasDraft: true, hasPublished: false }
 */
export function getConnectionEnvironmentLabels(
  connection: V2BookConnectionDescriptor | null | undefined,
): IConnectionEnvironmentLabels {
  const hasLabel = (key: string) =>
    !!connection?.labels?.some(
      label => label.key === key && label.value === 'true',
    );

  return {
    hasDraft: hasLabel('draft_connection'),
    hasPublished: hasLabel('published_connection'),
  };
}

export interface IPromoteDraftToProductionConnectionParams {
  updateConnection: (
    connection: V2BookConnectionDescriptor,
    credentials: Record<string, string>,
    integration: Bdkv2BookDescriptor,
    configMap?: Record<string, Bdkv2Value>,
  ) => Promise<V2BookConnectionResponse>;
  draftConnection: V2BookConnectionDescriptor;
  credentials: Record<string, string>;
  integration: Bdkv2BookDescriptor;
  configMap?: Record<string, Bdkv2Value>;
}

/**
 * The label set that makes one connection serve both environments: every
 * non-environment label it already carries, plus exactly one
 * `draft_connection` and one `published_connection`. Existing environment
 * labels are dropped first so a connection that already has one does not end up
 * with a duplicate.
 *
 * @param connection - The connection whose labels are being rewritten
 * @returns The labels to persist
 */
export function getBothEnvironmentsLabels(
  connection: V2BookConnectionDescriptor,
): Bdkv2Label[] {
  const labelsWithoutEnv = (connection.labels ?? []).filter(
    label => !DRAFT_PUBLISHED_LABEL_KEYS.has(label.key ?? ''),
  );
  return [
    ...labelsWithoutEnv,
    { key: 'draft_connection', value: 'true' },
    { key: 'published_connection', value: 'true' },
  ];
}

/**
 * Promotes an existing draft connection to serve both environments by updating
 * it with both `draft_connection` and `published_connection` labels. Dedups
 * any existing env labels so the result has exactly one of each.
 */
export async function promoteDraftToProductionConnection({
  updateConnection,
  draftConnection,
  credentials,
  integration,
  configMap,
}: IPromoteDraftToProductionConnectionParams): Promise<V2BookConnectionResponse> {
  return updateConnection(
    {
      ...draftConnection,
      labels: getBothEnvironmentsLabels(draftConnection),
    },
    credentials,
    integration,
    configMap,
  );
}

/** Reason an OAuth callback refused to create the connection, when recognized. */
/**
 * Upper bound on decode passes. Two is enough for every payload we've seen
 * (the backend encodes once and the redirect encodes again); the extra pass is
 * slack. The cap keeps a hostile or over-encoded value from spinning.
 */
const MAX_OAUTH_ERROR_DECODE_PASSES = 3;

const PERCENT_ESCAPE_PATTERN = /%[0-9a-f]{2}/i;

/**
 * Unwraps the percent-encoding the OAuth callback leaves on its `?error=`
 * message. React Router decodes the query string once, but the backend sends
 * the message already encoded, so what reaches the app still reads as
 * "A%20book%20connection%20already%20exists...". Decoding repeats until the
 * value stops changing, up to `MAX_OAUTH_ERROR_DECODE_PASSES`.
 *
 * The result is NOT guaranteed to be free of percent escapes: a value encoded
 * more times than the cap, or one with a malformed escape, comes back still
 * encoded. Callers must not treat the return value as presentable text — the
 * intended uses are pattern matching (`parseOAuthCallbackError`) and Sentry
 * titles, where a partly-encoded fallback is acceptable. User-facing copy
 * should come from a translation key, not from this string.
 *
 * @param rawError - Value of the `error` search param
 * @returns The message with percent escapes resolved as far as the cap allows,
 *   or the input unchanged when it is already readable or cannot be decoded
 */
export function decodeOAuthCallbackError(rawError: string): string {
  let decoded = rawError;

  for (let pass = 0; pass < MAX_OAUTH_ERROR_DECODE_PASSES; pass++) {
    if (!PERCENT_ESCAPE_PATTERN.test(decoded)) break;

    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      // Malformed escape — keep the last value that decoded cleanly.
      break;
    }

    if (next === decoded) break;
    decoded = next;
  }

  return decoded;
}

/**
 * Recognizes the duplicate-connection failures the OAuth callback reports
 * through `?error=`, which are expected outcomes rather than crashes: the
 * connection the user signed in for already exists and is usable. The most
 * common trigger is a replayed callback — navigating back after a successful
 * sign-in re-runs the authorization, and the second run collides with the
 * connection the first one created.
 *
 * Shares `parseDuplicateConnectionMessage` with `getDuplicateConnectionError`,
 * adding only the decode step, since the param arrives percent-encoded.
 *
 * @param rawError - Value of the `error` search param, encoded or not
 * @returns The reason and existing connection id, or undefined when the message
 *   is any other OAuth failure
 */
export function parseOAuthCallbackError(
  rawError: string,
): DuplicateConnectionError | undefined {
  return parseDuplicateConnectionMessage(decodeOAuthCallbackError(rawError));
}

/**
 * Where to send someone who reached an automation through one of its
 * connections.
 *
 * Prefers the draft editor whenever a draft exists, even for an automation that
 * is also published: someone arriving from a connection is far more likely to
 * want to edit the automation than to read its published run.
 *
 * @param automation - The automation's id and the stages it exists in
 * @param organizationId - Current organization ID
 * @param workspaceId - Current workspace ID
 * @returns Path to the draft editor, or to the automation details page
 */
export function getConnectionAutomationRoutePath(
  automation: { id: string; stages: V1AutomationStage[] },
  organizationId: string,
  workspaceId: string,
): string {
  const isDraft = automation.stages.includes(
    V1AutomationStage.AUTOMATION_STAGE_DRAFT,
  );

  return getRoutePath(isDraft ? 'DRAFT_EDITOR' : 'AUTOMATION_DETAILS', {
    orgId: organizationId,
    workspaceId,
    [isDraft ? 'draftId' : 'automationId']: automation.id,
  });
}
